import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { getTranslation } from "../services/translations";
import "./Navbar.css";

const ChevSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="11" height="11">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const Navbar = () => {
  const { language, setLanguage } = useLanguage();
  const t = k => getTranslation(language, k);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDD, setOpenDD] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const fn = () => { if (window.innerWidth > 960) setMenuOpen(false); };
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const fn = e => { if (navRef.current && !navRef.current.contains(e.target)) setOpenDD(null); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const goHome = id => {
    setMenuOpen(false);
    setOpenDD(null);
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const NAV = [
    { label: t("home"), action: () => goHome("home") },
    { label: t("aboutUs2"), dd: [
      { label: t("aboutUs2"), desc: t("ourStoryMission"), to: "/about" },
      { label: t("directorMessage"), desc: t("fromDrAshok"), to: "/director-message" },
    ]},
    { label: t("services"), wide: true, dd: [
      { label: t("endoscopy"), desc: t("upperGIExam"), to: "/services/endoscopy" },
      { label: t("colonoscopy"), desc: t("colonScreening"), to: "/services/colonoscopy" },
      { label: t("ercp"), desc: t("bileDuctProc"), to: "/services/ercp" },
      { label: t("biopsy"), desc: t("tissueSampling"), to: "/services/biopsy" },
      { label: t("varicealLigation"), desc: t("bleedingControl"), to: "/services/varicealLigation" },
      { label: t("foreignBodyRemoval"), desc: t("safeRetrieval"), to: "/services/foreignBodyRemoval" },
      { label: t("enteralMetalStenting"), desc: t("stentTherapy"), to: "/services/enteralMetalStenting" },
      { label: t("pegPlacement"), desc: t("feedingTube"), to: "/services/pegPlacement" },
      { label: t("polypectomy"), desc: t("polypRemoval"), to: "/services/polypectomy" },
      { label: t("hemoclipApplication"), desc: t("bleedingClipping"), to: "/services/hemoclipApplication" },
      { label: t("estUlcer"), desc: t("ulcerTreatment"), to: "/services/estUlcer" },
      { label: t("strictureDilatation"), desc: t("narrowingCorrection"), to: "/services/strictureDilatation" },
      { label: t("allServicesArrow"), action: () => goHome("services") },
    ]},
    { label: t("teamLabel"), to: "/team" },
    { label: t("facilities"), wide: true, dd: [
      { label: t("facilityOTTitle"), desc: t("advancedSurgical"), to: "/facilities/ot" },
      { label: t("facilityConsultTitle"), desc: t("privateConsult"), to: "/facilities/consult" },
      { label: t("facilityWaitingTitle"), desc: t("patientLounge"), to: "/facilities/waiting" },
      { label: t("facilityEmergencyTitle"), desc: t("emergencyCare247"), to: "/facilities/emergency" },
      { label: t("facilityReceptionTitle"), desc: t("patientReg"), to: "/facilities/reception" },
      { label: t("facilityLabTitle"), desc: t("roundClockTesting"), to: "/facilities/lab" },
      { label: t("facilityPharmacyTitle"), desc: t("medicinesAvail"), to: "/facilities/pharmacy" },
      { label: t("facilityParkingTitle"), desc: t("freeParkingDesc"), to: "/facilities/parking" },
      { label: t("allFacilitiesArrow"), action: () => goHome("facilities") },
    ]},
    { label: t("contact"), action: () => goHome("contact") },
  ];

  return (
    <>
      {/* Layer 1: Top Bar */}
      <div className="nb-top-bar">
        <div className="nb-top-bar-inner">
          <div className="nb-top-bar-left">
            <Link to="/book" className="nb-top-bar-btn">{t("bookEAppointment")}</Link>
            <span className="nb-top-bar-divider"/>
            <span className="nb-top-bar-emergency">{t("emergencyColon")} <strong>+91 9996504991</strong></span>
          </div>
          <div className="nb-top-bar-right">
            <a href="https://www.facebook.com/people/Ashok-Gastro-Liver-Centre/61569483430200/#" target="_blank" rel="noopener noreferrer" className="nb-top-social" aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
            </a>
            <a href="https://www.instagram.com/dr.ashok.gastro/" target="_blank" rel="noopener noreferrer" className="nb-top-social" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </a>
            <a href="https://www.linkedin.com/in/dr-ashok-kumar-761733275/" target="_blank" rel="noopener noreferrer" className="nb-top-social" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
            </a>
            <span className="nb-top-bar-divider"/>
            <div className="nb-lang-switcher">
              <button className={`nb-lang-btn${language==="en"?" active":""}`} onClick={()=>setLanguage("en")}>EN</button>
              <button className={`nb-lang-btn${language==="hi"?" active":""}`} onClick={()=>setLanguage("hi")}>HI</button>
            </div>
          </div>
        </div>
      </div>

      {/* Layer 2: Logo Header */}
      <div className="nb-logo-header">
        <div className="nb-logo-header-inner">
          <div className="nb-logo-section">
            <div className="nb-logo-icon">
              <img src={`${process.env.PUBLIC_URL}/logo1.png`} alt="Logo" className="nb-logo-img"/>
            </div>
            <div className="nb-logo-text-inline">
              <span className="nb-logo-main-text">{t("hospitalName")}</span>
              <span className="nb-logo-sub-text">{t("hospitalTagline")}</span>
            </div>
          </div>
          <div className="nb-header-contact-strip">
            <div className="nb-header-contact-item">
              <div className="nb-hci-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              </div>
              <div><span className="nb-hci-label">{t("callUs")}</span><span className="nb-hci-val">+91 9996504991</span></div>
            </div>
            <div className="nb-header-contact-item">
              <div className="nb-hci-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div><span className="nb-hci-label">{t("emergency")}</span><span className="nb-hci-val">{t("available247")}</span></div>
            </div>
            <div className="nb-header-contact-item">
              <div className="nb-hci-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div><span className="nb-hci-label">{t("locationNav")}</span><span className="nb-hci-val">{t("narnaulHaryana")}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Layer 3: Nav */}
      <div className={`nb-navbar-wrapper${scrolled?" scrolled":""}`} ref={navRef}>
        <nav className="nb-navbar">
          <ul className="nb-nav-menu">
            {NAV.map((item, idx) => (
              <li key={idx} className={item.dd?"nb-nav-item-has-dropdown":""}>
                {item.dd ? (
                  <>
                    <button className={`nb-nav-link${openDD===idx?" active":""}`} onClick={()=>setOpenDD(p=>p===idx?null:idx)}>
                      {item.label} <span className="nb-nav-chevron"><ChevSVG/></span>
                    </button>
                    {openDD===idx && (
                      <div className={`nb-nav-dropdown${item.wide?" nb-dd-wide":""}`}>
                        {item.dd.map((s,si)=>s.to
                          ? <Link key={si} to={s.to} className="nb-nav-dropdown-item" onClick={()=>setOpenDD(null)}>
                              <span className="nb-nav-dd-label">{s.label}</span>
                              {s.desc && <span className="nb-nav-dd-desc">{s.desc}</span>}
                            </Link>
                          : <button key={si} className="nb-nav-dropdown-item" onClick={()=>{s.action();setOpenDD(null);}}>
                              <span className="nb-nav-dd-label">{s.label}</span>
                              {s.desc && <span className="nb-nav-dd-desc">{s.desc}</span>}
                            </button>
                        )}
                      </div>
                    )}
                  </>
                ) : item.to ? (
                  <Link to={item.to} className="nb-nav-link">{item.label}</Link>
                ) : (
                  <button className="nb-nav-link" onClick={item.action}>{item.label}</button>
                )}
              </li>
            ))}
            <li><Link to="/book" className="nb-nav-book-btn">{t("bookAppointment")}</Link></li>
          </ul>
          <button className="nb-hamburger" onClick={()=>setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span style={{transform:menuOpen?"rotate(45deg) translate(5px,5px)":"none"}}/>
            <span style={{opacity:menuOpen?0:1}}/>
            <span style={{transform:menuOpen?"rotate(-45deg) translate(5px,-5px)":"none"}}/>
          </button>
        </nav>
        {menuOpen && (
          <div className="nb-mobile-nav">
            <button className="nb-nav-link" onClick={()=>goHome("home")}>{t("home")}</button>
            <Link to="/about" className="nb-nav-link" onClick={()=>setMenuOpen(false)}>{t("aboutUs2")}</Link>
            <Link to="/director-message" className="nb-nav-link" onClick={()=>setMenuOpen(false)}>{t("directorMessage")}</Link>
            <button className="nb-nav-link" onClick={()=>goHome("services")}>{t("allServicesArrow")}</button>
            <Link to="/team" className="nb-nav-link" onClick={()=>setMenuOpen(false)}>{t("teamLabel")}</Link>
            <button className="nb-nav-link" onClick={()=>goHome("facilities")}>{t("facilities")}</button>
            <button className="nb-nav-link" onClick={()=>goHome("contact")}>{t("contact")}</button>
            <Link to="/book" className="nb-nav-book-btn" onClick={()=>setMenuOpen(false)}>{t("bookAppointment")}</Link>
          </div>
        )}
      </div>
    </>
  );
};

export default Navbar;
